import React, { useState } from 'react';
import { loadDemoSymptoms } from '../../demo-data/symptoms';
import { Plus, Brain, TrendingUp, Clock, Target } from 'lucide-react';

interface Symptom {
  id: string;
  name: string;
  category: string;
  severity: number;
  date: string;
  time: string;
  notes?: string;
  triggers?: string[];
  relatedConditions?: string[];
  aiSuggested?: boolean;
}

interface SymptomSuggestion {
  name: string;
  category: string;
  reason: string;
  confidence: number;
  relatedTo: string[];
}

interface UserCondition {
  id: string;
  name: string;
  category: string;
}

export const AdvancedSymptomTracker: React.FC = () => {
  const [symptoms, setSymptoms] = useState<Symptom[]>(loadDemoSymptoms());

  const [userConditions] = useState<UserCondition[]>([
    { id: '1', name: 'Type 2 Diabetes Mellitus', category: 'Endocrine' },
    { id: '2', name: 'Hypertension', category: 'Cardiovascular' },
    { id: '3', name: 'Generalized Anxiety Disorder', category: 'Mental Health' },
  ]);

  const [aiSuggestions] = useState<SymptomSuggestion[]>([
    {
      name: 'Blurred Vision',
      category: 'Ophthalmological',
      reason: 'Common symptom associated with diabetes management',
      confidence: 85,
      relatedTo: ['Type 2 Diabetes'],
    },
    {
      name: 'Heart Palpitations',
      category: 'Cardiovascular',
      reason: 'May be related to anxiety and hypertension',
      confidence: 78,
      relatedTo: ['Generalized Anxiety Disorder', 'Hypertension'],
    },
    {
      name: 'Mood Changes',
      category: 'Mental Health',
      reason: 'Blood sugar fluctuations can affect mood',
      confidence: 72,
      relatedTo: ['Type 2 Diabetes', 'Generalized Anxiety Disorder'],
    },
  ]);

  const [showAISuggestions, setShowAISuggestions] = useState(false);

  const symptomCategories = [
    'General', 'Cardiovascular', 'Respiratory', 'Gastrointestinal', 'Neurological',
    'Musculoskeletal', 'Dermatological', 'Endocrine', 'Mental Health', 'Ophthalmological',
    'Urological', 'Gynecological', 'ENT', 'Hematological', 'Immunological'
  ];

  const getSeverityColor = (severity: number) => {
    if (severity <= 3) return 'text-green-600 bg-green-50 border-green-200';
    if (severity <= 6) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    if (severity <= 8) return 'text-orange-600 bg-orange-50 border-orange-200';
    return 'text-red-600 bg-red-50 border-red-200';
  };

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      'General': 'bg-slate-100 text-slate-800',
      'Cardiovascular': 'bg-red-100 text-red-800',
      'Respiratory': 'bg-blue-100 text-blue-800',
      'Gastrointestinal': 'bg-green-100 text-green-800',
      'Neurological': 'bg-purple-100 text-purple-800',
      'Mental Health': 'bg-indigo-100 text-indigo-800',
      'Endocrine': 'bg-orange-100 text-orange-800',
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-slate-800">Advanced Symptom Tracking</h2>
          <p className="text-slate-600 mt-1">AI-powered symptom tracking customized for your conditions</p>
        </div>
        <div className="flex items-center space-x-3">
          <button 
            onClick={() => setShowAISuggestions(!showAISuggestions)}
            className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
          >
            <Brain className="h-4 w-4" />
            <span>AI Suggestions</span>
          </button>
          <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors">
            <Plus className="h-4 w-4" />
            <span>Log Symptom</span>
          </button>
        </div>
      </div>

      {/* AI Suggestions Panel */}
      {showAISuggestions && (
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl border border-purple-200 p-6">
          <div className="flex items-center space-x-3 mb-4">
            <Brain className="h-6 w-6 text-purple-600" />
            <h3 className="text-lg font-semibold text-slate-800">AI-Suggested Symptoms to Track</h3>
          </div>
          <p className="text-sm text-slate-600 mb-4">
            Based on your conditions and recent patterns, these symptoms might be relevant to monitor:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {aiSuggestions.map((suggestion, index) => (
              <div key={index} className="bg-white rounded-lg p-4 border border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-slate-800">{suggestion.name}</h4>
                  <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">
                    {suggestion.confidence}% confidence
                  </span>
                </div>
                <p className="text-sm text-slate-600 mb-2">{suggestion.reason}</p>
                <div className="flex flex-wrap gap-1 mb-3">
                  {suggestion.relatedTo.map((condition, idx) => (
                    <span key={idx} className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">
                      {condition}
                    </span>
                  ))}
                </div>
                <button className="w-full px-3 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 transition-colors">
                  Add to Tracking
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Today's Symptoms</p>
              <p className="text-2xl font-bold text-slate-800">{symptoms.filter(s => s.date === '2024-01-15').length}</p>
            </div>
            <Clock className="h-8 w-8 text-blue-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">AI Suggestions</p>
              <p className="text-2xl font-bold text-slate-800">{aiSuggestions.length}</p>
            </div>
            <Brain className="h-8 w-8 text-purple-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Categories Tracked</p>
              <p className="text-2xl font-bold text-slate-800">5</p>
            </div>
            <Target className="h-8 w-8 text-green-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Avg Severity</p>
              <p className="text-2xl font-bold text-slate-800">4.2</p>
            </div>
            <TrendingUp className="h-8 w-8 text-orange-500" />
          </div>
        </div>
      </div>

      {/* Condition-Based Symptom Categories */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Symptoms by Your Conditions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {userConditions.map((condition) => (
            <div key={condition.id} className="border border-slate-200 rounded-lg p-4">
              <h4 className="font-medium text-slate-800 mb-2">{condition.name}</h4>
              <div className="space-y-2">
                {symptoms
                  .filter(s => s.relatedConditions?.includes(condition.name))
                  .map((symptom) => (
                    <div key={symptom.id} className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">{symptom.name}</span>
                      <span className={`px-2 py-1 text-xs rounded-full border ${getSeverityColor(symptom.severity)}`}>
                        {symptom.severity}/10
                      </span>
                    </div>
                  ))}
              </div>
              <button className="w-full mt-3 text-sm text-blue-600 hover:text-blue-800 font-medium">
                + Add symptom for {condition.name}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Symptoms with AI Insights */}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-slate-800">Recent Symptoms</h3>
            <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
              Generate AI Analysis
            </button>
          </div>
        </div>
        <div className="divide-y divide-slate-200">
          {symptoms.map((symptom) => (
            <div key={symptom.id} className="p-6 hover:bg-slate-50 transition-colors">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h4 className="text-lg font-medium text-slate-800">{symptom.name}</h4>
                    {symptom.aiSuggested && (
                      <span className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded-full flex items-center space-x-1">
                        <Brain className="h-3 w-3" />
                        <span>AI Suggested</span>
                      </span>
                    )}
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getCategoryColor(symptom.category)}`}>
                      {symptom.category}
                    </span>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getSeverityColor(symptom.severity)}`}>
                      Severity: {symptom.severity}/10
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-slate-600 mb-2">
                    <div className="flex items-center space-x-2">
                      <Clock className="h-4 w-4" />
                      <span>{new Date(symptom.date).toLocaleDateString()} at {symptom.time}</span>
                    </div>
                    {symptom.relatedConditions && (
                      <div className="flex items-center space-x-2">
                        <Target className="h-4 w-4" />
                        <span>Related to {symptom.relatedConditions.join(', ')}</span>
                      </div>
                    )}
                  </div>

                  {symptom.notes && (
                    <p className="text-sm text-slate-600 mb-2">{symptom.notes}</p>
                  )}

                  {symptom.triggers && symptom.triggers.length > 0 && (
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="text-xs text-slate-500">Triggers:</span>
                      {symptom.triggers.map((trigger, index) => (
                        <span key={index} className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded-full">
                          {trigger}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* AI Correlation Insights */}
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center space-x-2 mb-2">
                      <Brain className="h-4 w-4 text-blue-600" />
                      <span className="text-sm font-medium text-blue-800">AI Insights</span>
                    </div>
                    <p className="text-xs text-blue-700">
                      This symptom correlates with your diabetes management. Consider checking blood glucose levels.
                      Pattern analysis shows 73% correlation with sleep quality.
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <button className="px-3 py-1 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                    Edit
                  </button>
                  <button className="px-3 py-1 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors">
                    AI Analysis
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Add Symptom Categories */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Quick Add by Category</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {symptomCategories.map((category) => (
            <button
              key={category}
              className={`p-3 text-sm font-medium rounded-lg border transition-colors hover:bg-slate-50 ${getCategoryColor(category)}`}
            >
              {category}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
